import { toast } from '@heroui/react';
/**
 * 空间应用：按应用类型分组，卡片 + Switch 控制启用状态。
 */
import { useMemo, useState } from 'react';
import { useAppSelector } from '@/stores/hooks';
import { trpc } from '@/lib/trpc';
import { IconArrowUpRight } from '@tabler/icons-react';

import { Button, Card, Description, Label, Switch } from '@heroui/react';
import { SpaceAppDetailSheet } from './SpaceAppDetailSheet';

const APP_TYPE_LABELS: Record<string, string> = {
  SYSTEM_MANAGEMENT: '系统管理',
  SUBSCRIPTION_MANAGEMENT: '订阅管理',
  WORKSPACE: '工作空间',
};

type AppItem = {
  spaceAppId: string;
  appId: string;
  appCode?: string | null;
  name: string;
  icon?: string | null;
  type?: string | null;
  description?: string | null;
  isEnabled: boolean;
  isPublic: boolean;
};

function groupAppsByType(apps: AppItem[]): Array<{ type: string; label: string; apps: AppItem[] }> {
  const byType = new Map<string, AppItem[]>();
  for (const app of apps) {
    const key = app.type ?? 'OTHER';
    if (!byType.has(key)) byType.set(key, []);
    byType.get(key)!.push(app);
  }
  const order = ['SYSTEM_MANAGEMENT', 'SUBSCRIPTION_MANAGEMENT', 'WORKSPACE'];
  const result: Array<{ type: string; label: string; apps: AppItem[] }> = [];
  for (const type of order) {
    const list = byType.get(type);
    if (list?.length) {
      result.push({
        type,
        label: APP_TYPE_LABELS[type] ?? type,
        apps: list,
      });
    }
  }
  const rest = Array.from(byType.entries()).filter(([k]) => !order.includes(k));
  for (const [type, list] of rest) {
    result.push({ type, label: APP_TYPE_LABELS[type] ?? type, apps: list });
  }
  return result;
}

export function SpaceAppsPanel() {
  const [manageApp, setManageApp] = useState<AppItem | null>(null);
  const [updatingSpaceAppIds, setUpdatingSpaceAppIds] = useState<Set<string>>(() => new Set());
  const user = useAppSelector((state) => state.auth.user);
  const spaceId = user?.spaces?.[0]?.id;
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const { data: spaceAppsList, isLoading } = trpc.spaceApps.listAll.useQuery(
    { spaceId: spaceId! },
    { enabled: isHydrated && isAuthenticated && !!spaceId }
  );

  const utils = trpc.useUtils();
  const updateMutation = trpc.spaceApps.update.useMutation({
    onMutate: (variables) => {
      setUpdatingSpaceAppIds((prev) => new Set(prev).add(String(variables.id)));
    },
    onSuccess: (_, variables) => {
      void utils.spaceApps.listAll.invalidate({ spaceId: spaceId! });
      toast.success(variables.data.isEnabled ? '已启用' : '已禁用');
    },
    onError: (e) => toast.danger(e.message ?? '更新失败'),
    onSettled: (_data, _error, variables) => {
      if (!variables?.id) return;
      setUpdatingSpaceAppIds((prev) => {
        const next = new Set(prev);
        next.delete(String(variables.id));
        return next;
      });
    },
  });

  const groups = useMemo(() => {
    const apps: AppItem[] = (spaceAppsList ?? [])
      .filter((row): row is typeof row & { app: NonNullable<typeof row.app> } => row.app != null && row.app.id != null)
      .map((row) => ({
        spaceAppId: (row.id as string) ?? '',
        appId: row.app.id as string,
        appCode: row.app.code ?? null,
        name: row.app.name ?? '',
        icon: row.app.icon ?? null,
        type: row.app.type ?? null,
        description: row.app.description ?? null,
        isEnabled: row.isEnabled ?? false,
        isPublic: row.isPublic ?? true,
      }));
    return groupAppsByType(apps);
  }, [spaceAppsList]);

  if (!spaceId) {
    return (
      <div className="flex flex-1 flex-col overflow-y-auto p-6">
        <p className="text-muted-foreground text-sm">暂无空间信息，无法加载应用列表。</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col overflow-y-auto p-6">
        <p className="text-muted-foreground text-sm">加载中…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-y-auto p-4 md:p-6">
      <div className="mx-auto w-full max-w-4xl space-y-8">
        <div>
          <h3 className="text-lg font-semibold">应用管理</h3>
          <Description>开启或关闭当前空间可用的应用</Description>
        </div>

        {groups.length === 0 ? (
          <p className="text-muted-foreground text-sm">当前空间下暂无应用。</p>
        ) : (
          groups.map(({ type, label, apps }) => (
            <section key={type} className="space-y-3">
              <h3 className="text-base font-semibold">{label}</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {apps.map((app) => (
                  <Card key={app.spaceAppId} className="transition-all">
                    <Card.Content className="flex flex-col gap-2">
                      <div className="min-w-0">
                        <Label className="text-sm font-semibold leading-tight">{app.name}</Label>
                        {app.description ? (
                          <Description className="mt-1 line-clamp-2 text-xs">{app.description}</Description>
                        ) : null}
                      </div>
                    </Card.Content>
                    <Card.Footer className="border-border flex items-center justify-between border-t pt-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          isDisabled={updatingSpaceAppIds.has(app.spaceAppId)}
                          isSelected={app.isEnabled}
                          onChange={(enabled) => {
                            updateMutation.mutate({
                              id: app.spaceAppId,
                              data: { isEnabled: enabled },
                            });
                          }}
                          aria-label={`${app.name} 启用状态`}
                        >
                          <Switch.Content>
                            <Switch.Control>
                              <Switch.Thumb />
                            </Switch.Control>
                          </Switch.Content>
                        </Switch>
                        <span className="text-muted-foreground text-sm">{app.isEnabled ? '启用' : '禁用'}</span>
                      </div>
                      <Button size="sm" variant="ghost" onPress={() => setManageApp(app)}>
                        授权管理
                        <IconArrowUpRight stroke={1.5} />
                      </Button>
                    </Card.Footer>
                  </Card>
                ))}
              </div>
            </section>
          ))
        )}
      </div>

      {manageApp ? (
        <SpaceAppDetailSheet
          key={manageApp.appId}
          open
          spaceAppId={manageApp.spaceAppId}
          appId={manageApp.appId}
          appCode={manageApp.appCode}
          isPublic={manageApp.isPublic}
          summary={{
            name: manageApp.name,
            description: manageApp.description,
            icon: manageApp.icon,
          }}
          onOpenChange={(next) => {
            if (!next) setManageApp(null);
          }}
        />
      ) : null}
    </div>
  );
}
